import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { icons, LucideIcon } from "lucide-react";

// Curated list of icons useful for construction/logistics/general tagging.
// These are PascalCase keys matching the lucide-react `icons` export.
const CURATED_ICONS: string[] = [
  // Construction & Materials
  "HardHat", "Hammer", "Wrench", "Shovel", "Blocks", "Brick",
  "Factory", "Building", "Building2", "Warehouse", "Landmark",
  // Transport & Vehicles
  "Truck", "Car", "Ship", "Train", "Forklift", "Container",
  // Nature & Quarry
  "Mountain", "Trees", "Droplets", "Waves", "Sun", "Cloud",
  // Status & Priority
  "Flag", "AlertTriangle", "AlertCircle", "CheckCircle", "XCircle",
  "Star", "Heart", "ThumbsUp", "ThumbsDown", "Zap", "Flame",
  "Clock", "Timer", "Hourglass", "CalendarCheck",
  // Categories
  "Tag", "Tags", "Bookmark", "Folder", "FolderOpen", "Archive",
  "Package", "PackageCheck", "Box", "Boxes",
  // People & Roles
  "User", "Users", "UserCheck", "HardHat",
  // Tools & Actions
  "Search", "Filter", "Settings", "Cog", "Gauge",
  "Clipboard", "ClipboardCheck", "FileText", "FileCheck",
  // Arrows & Navigation
  "ArrowUp", "ArrowDown", "ArrowRight", "ArrowLeft",
  "TrendingUp", "TrendingDown", "MoveUp", "MoveDown",
  // Misc
  "CircleDot", "Target", "Crosshair", "Eye", "Shield",
  "Lock", "Unlock", "Key", "Bell", "MapPin", "Map",
  "Ruler", "Scale", "Weight", "Layers", "Grid3x3",
  "Hash", "AtSign", "DollarSign", "Percent", "Receipt", "CircleCheck",
];

// Deduplicate
const ICON_NAMES = Array.from(new Set(CURATED_ICONS)).filter((name) => name in icons);

const POPOVER_GAP = 6;

interface IconPickerProps {
  value: string | null;
  onChange: (icon: string | null) => void;
}

export const IconPicker: React.FC<IconPickerProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return ICON_NAMES;
    const q = search.toLowerCase();
    return ICON_NAMES.filter((name) => name.toLowerCase().includes(q));
  }, [search]);

  const SelectedIcon = value && value in icons ? (icons as Record<string, LucideIcon>)[value] : null;

  // Measure & position the popover, preferring below but flipping above if not enough room.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !popoverRef.current) return;
    const btnRect = buttonRef.current.getBoundingClientRect();
    const popRect = popoverRef.current.getBoundingClientRect();

    // Try below first
    let top = btnRect.bottom + POPOVER_GAP;
    if (top + popRect.height > window.innerHeight - 8) {
      // Not enough room below — place above
      top = btnRect.top - popRect.height - POPOVER_GAP;
    }
    // If above would clip the top, fall back to below anyway
    if (top < 8) top = btnRect.bottom + POPOVER_GAP;

    // Align left edge with button, clamp to viewport
    let left = btnRect.left;
    const maxLeft = window.innerWidth - popRect.width - 8;
    if (left > maxLeft) left = maxLeft;
    if (left < 8) left = 8;

    setPos({ top, left });
  }, [open, filtered.length]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-background-dark text-slate-700 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-600 transition-colors"
      >
        {SelectedIcon ? (
          <>
            <SelectedIcon size={16} />
            <span className="max-w-[80px] truncate">{value}</span>
          </>
        ) : (
          <span className="text-slate-400">Icon</span>
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              visibility: pos ? "visible" : "hidden",
            }}
            className="w-72 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl p-3 z-[110]"
          >
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search icons..."
              autoFocus
              className="w-full px-2.5 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-background-dark text-slate-900 dark:text-white mb-2"
            />
            <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
              {value && (
                <button
                  onClick={() => { onChange(null); setOpen(false); }}
                  className="w-8 h-8 flex items-center justify-center rounded-md border border-dashed border-slate-300 dark:border-slate-600 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 text-xs"
                  title="Clear"
                >
                  ✕
                </button>
              )}
              {filtered.map((name) => {
                const Icon = (icons as Record<string, LucideIcon>)[name];
                if (!Icon) return null;
                return (
                  <button
                    key={name}
                    onClick={() => { onChange(name); setOpen(false); setSearch(""); }}
                    className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
                      value === name
                        ? "bg-primary text-white"
                        : "hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300"
                    }`}
                    title={name}
                  >
                    <Icon size={16} />
                  </button>
                );
              })}
            </div>
            {filtered.length === 0 && (
              <div className="text-xs text-slate-400 text-center py-2">No matching icons</div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

/** Render a lucide icon by name. Returns null if the name is invalid. */
export const DynamicIcon: React.FC<{ name: string; size?: number; className?: string }> = ({
  name,
  size = 16,
  className,
}) => {
  const Icon = name in icons ? (icons as Record<string, LucideIcon>)[name] : null;
  if (!Icon) return null;
  return <Icon size={size} className={className} />;
};
